// Analytics fan-out.
//
// Wraps GA4 (react-ga4) and PostHog behind one call so both receive the same
// events during the migration. Call sites use trackEvent() with the exact same
// argument shape as ReactGA.event({category, action, label, value}), so porting
// an existing call is an import swap and nothing else.
//
// PostHog is a no-op unless REACT_APP_POSTHOG_KEY is set, which keeps local dev
// out of the production project by default.

import ReactGA from 'react-ga4';
import posthog from 'posthog-js';

const GA_TRACKING_ID = "G-3R8WJPRNSR";
const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY;
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';
const isDev = process.env.NODE_ENV === "development";

let posthogReady = false;

export const initAnalytics = ()=>{
  ReactGA.initialize(GA_TRACKING_ID,{
    debug: isDev,
    siteSpeedSampleRate: '100%'
  });

  if (!POSTHOG_KEY) {
    if (isDev) console.log('[analytics] no REACT_APP_POSTHOG_KEY, PostHog disabled');
    return;
  }

  posthog.init(POSTHOG_KEY,{
    api_host: POSTHOG_HOST,
    ui_host: 'https://us.posthog.com',
    // This app is a react-three-fiber canvas. Autocapture listens for DOM
    // clicks/inputs, which against a WebGL canvas yields noise rather than
    // signal while adding listener overhead to a frame-budget-sensitive scene.
    // Everything worth knowing is captured explicitly via trackEvent below.
    autocapture: false,
    // Session replay records DOM mutations, so a canvas-driven scene replays as
    // a static box. Not worth the payload.
    disable_session_recording: true,
    // Pageviews are sent from PageViewTracker so SPA route changes are tied to
    // the react-router location rather than raw history events.
    capture_pageview: false,
    debug: isDev
  });
  posthogReady = true;
}

// Hands delivery to the browser so the event survives the page going away. Only
// use when the document actually unloads (a form post that navigates off-site),
// because sendBeacon fails SILENTLY: the browser can refuse to queue it and
// posthog has no XHR fallback for a single event, so a dropped event leaves no
// trace anywhere. Anything that stays on the page should use the default.
export const UNLOAD_SAFE = {transport: 'sendBeacon', send_instantly: true};

// Mirrors ReactGA.event({category, action, label, value}).
// GA keeps the category/action/label triple, since that is all its schema
// accepts. PostHog uses the action as the event name (the most specific field)
// and takes any additional keys as first-class properties — those are what you
// can actually filter and break down on, so prefer them over packing values
// into `label`.
export const trackEvent = ({category,action,label,value,...props},options)=>{
  ReactGA.event({category,action,label,value});

  if (!posthogReady) return;
  posthog.capture(action,{
    category,
    ...(label !== undefined && {label}),
    ...(value !== undefined && {value}),
    ...props
  },options);
}

export const trackPageView = ({path,title})=>{
  ReactGA.send({hitType:"pageview", page:path, title});

  if (!posthogReady) return;
  posthog.capture('$pageview',{
    $current_url: window.location.origin + path,
    ...(title !== undefined && {title})
  });
}

export default posthog;
